import { useEffect } from "react";

/**
 * Marks horizontally-clipped markdown tables so a chevron can show which way there is more to see.
 * Without it a table cut off at the bubble edge reads as truncated data rather than as scrollable.
 *
 * The table itself is the scrolling element, so a chevron drawn on it would scroll away with the content;
 * each table is therefore wrapped once in a non-scrolling positioned parent that carries the indicator.
 * Runs over rendered markdown, which is injected as HTML, so this walks the DOM rather than the source.
 */
export function useTableScrollHints(ref: { current: HTMLElement | null }, deps: unknown[]) {
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const cleanups: (() => void)[] = [];
    root.querySelectorAll("table").forEach((table) => {
      let wrap = table.parentElement;
      if (!wrap || !wrap.classList.contains("table-scroll")) {
        wrap = document.createElement("div");
        wrap.className = "table-scroll";
        table.parentNode?.insertBefore(wrap, table);
        wrap.appendChild(table);
      }
      const box = wrap;
      const update = () => {
        // A pixel of slack: fractional scroll positions otherwise leave the chevron on at the very end.
        const max = table.scrollWidth - table.clientWidth;
        box.classList.toggle("can-left", table.scrollLeft > 1);
        box.classList.toggle("can-right", table.scrollLeft < max - 1);
      };
      update();
      table.addEventListener("scroll", update, { passive: true });
      // The bubble can be laid out before its width settles, and a resize changes what fits.
      const ro = new ResizeObserver(update);
      ro.observe(table);
      cleanups.push(() => {
        table.removeEventListener("scroll", update);
        ro.disconnect();
      });
    });
    return () => cleanups.forEach((c) => c());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
